import type { RngState } from './types';

/**
 * Counter-based deterministic RNG.
 *
 * The nth value is a pure hash of (seed, n), so state is just two integers and
 * any client or server that knows the seed and call count produces the same
 * stream. Mixing is a 32-bit variant of splitmix / murmur finalisers.
 */

function mix(seed: number, n: number): number {
  let h = (seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (n >>> 0), 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h ^ ((n * 0x27d4eb2f) >>> 0), 0x165667b1) >>> 0;
  h ^= h >>> 15;
  return h >>> 0;
}

export function createRng(seed: number): RngState {
  return { seed: seed >>> 0, calls: 0 };
}

/** Uniform float in [0, 1). */
export function nextFloat(rng: RngState): [number, RngState] {
  const value = mix(rng.seed, rng.calls) / 0x1_0000_0000;
  return [value, { seed: rng.seed, calls: rng.calls + 1 }];
}

/** Uniform integer in [min, max] inclusive. */
export function nextInt(rng: RngState, min: number, max: number): [number, RngState] {
  if (!Number.isInteger(min) || !Number.isInteger(max) || max < min) {
    throw new RangeError(`nextInt: invalid range [${min}, ${max}]`);
  }
  const [f, next] = nextFloat(rng);
  return [min + Math.floor(f * (max - min + 1)), next];
}

/** Six-sided die. */
export function rollD6(rng: RngState): [number, RngState] {
  return nextInt(rng, 1, 6);
}

/** Bernoulli trial with probability `p`. */
export function chance(rng: RngState, p: number): [boolean, RngState] {
  const [f, next] = nextFloat(rng);
  return [f < p, next];
}

/** Fisher–Yates shuffle returning a new array. */
export function shuffle<T>(rng: RngState, items: readonly T[]): [T[], RngState] {
  const out = items.slice();
  let r = rng;
  for (let i = out.length - 1; i > 0; i--) {
    let j: number;
    [j, r] = nextInt(r, 0, i);
    const tmp = out[i] as T;
    out[i] = out[j] as T;
    out[j] = tmp;
  }
  return [out, r];
}

/** Uniformly pick one element. Throws on an empty list. */
export function pick<T>(rng: RngState, items: readonly T[]): [T, RngState] {
  if (items.length === 0) throw new RangeError('pick: empty list');
  const [i, next] = nextInt(rng, 0, items.length - 1);
  return [items[i] as T, next];
}
